import React, { useState, ChangeEvent, useEffect } from 'react';
import { Client, CoAuthor } from '../../types';
import { INDUSTRY_TYPES } from '../../constants/industries';
import { HelpButton } from '../common/HelpButton';
import { InfoModal } from '../common/InfoModal';

interface CompanyFormProps {
  client?: Client;
  onSave?: (client: Client) => void;
  industries?: string[];
}

export const CompanyForm: React.FC<CompanyFormProps> = ({ 
  client = { id: '', name: '', industry: '', profile: '', relationship: '', ERPAccountNumber: '', co_authors: [] },
  onSave,
  industries = INDUSTRY_TYPES
}) => {
  const [formData, setFormData] = useState<Client>(client);
  const [initialData, setInitialData] = useState<Client>(client);
  const [coAuthorInput, setCoAuthorInput] = useState('');
  const [profileChars, setProfileChars] = useState(client.profile?.length || 0);
  const [relationshipChars, setRelationshipChars] = useState(client.relationship?.length || 0);
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showErpInfo, setShowErpInfo] = useState(false);

  useEffect(() => {
    // Update form data and initial data when client prop changes
    setFormData(client);
    setInitialData(client);
    setProfileChars(client.profile?.length || 0);
    setRelationshipChars(client.relationship?.length || 0);
    setCoAuthorInput('');
    setFormErrors({});
  }, [client]);

  // Check if form data has changed from initial values
  const hasChanges = 
    formData.name !== initialData.name ||
    formData.industry !== initialData.industry ||
    (formData.ERPAccountNumber || '') !== (initialData.ERPAccountNumber || '') ||
    formData.profile !== (initialData.profile || '') ||
    formData.relationship !== (initialData.relationship || '') ||
    JSON.stringify(formData.co_authors || []) !== JSON.stringify(initialData.co_authors || []);

  // Check if character limits are exceeded
  const exceedsCharacterLimit = profileChars > 1700 || relationshipChars > 1700;

  const isValidPresidioEmail = (value: string) => {
    const normalized = value.trim().toLowerCase();
    return /^[a-z0-9._%+-]+@presidio\.com$/.test(normalized);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'profile') {
      setProfileChars(value.length);
      // Validate character limit
      if (value.length > 1700) {
        setFormErrors(prev => ({ ...prev, profile: 'Company profile must not exceed 1700 characters' }));
      } else {
        setFormErrors(prev => ({ ...prev, profile: '' }));
      }
    } else if (name === 'relationship') {
      setRelationshipChars(value.length);
      // Validate character limit
      if (value.length > 1700) {
        setFormErrors(prev => ({ ...prev, relationship: 'Relationship summary must not exceed 1700 characters' }));
      } else {
        setFormErrors(prev => ({ ...prev, relationship: '' }));
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is edited (for other fields)
    if (formErrors[name] && name !== 'profile' && name !== 'relationship') {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Company name is required';
    }
    
    if (!formData.industry) {
      errors.industry = 'Industry type is required';
    }
    
    if (profileChars > 1700) {
      errors.profile = 'Company profile must not exceed 1700 characters';
    }
    
    if (relationshipChars > 1700) {
      errors.relationship = 'Relationship summary must not exceed 1700 characters';
    }

    const invalidCoAuthor = (formData.co_authors || []).find((coAuthor) => !isValidPresidioEmail(coAuthor.email));
    if (invalidCoAuthor) {
      errors.co_authors = 'Co-authors must use a valid @presidio.com email address';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddCoAuthor = () => {
    const normalizedEmail = coAuthorInput.trim().toLowerCase();

    if (!normalizedEmail) {
      return;
    }

    if (!isValidPresidioEmail(normalizedEmail)) {
      setFormErrors(prev => ({ ...prev, co_authors: 'Enter a valid @presidio.com email address' }));
      return;
    }

    if ((formData.co_authors || []).some((coAuthor) => coAuthor.email === normalizedEmail)) {
      setFormErrors(prev => ({ ...prev, co_authors: 'That co-author has already been added' }));
      return;
    }

    const newCoAuthor: CoAuthor = {
      user_id: '',
      email: normalizedEmail,
    };

    setFormData(prev => ({
      ...prev,
      co_authors: [...(prev.co_authors || []), newCoAuthor],
    }));
    setCoAuthorInput('');
    setFormErrors(prev => ({ ...prev, co_authors: '' }));
  };

  const handleRemoveCoAuthor = (emailToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      co_authors: (prev.co_authors || []).filter((coAuthor) => coAuthor.email !== emailToRemove),
    }));
    setFormErrors(prev => ({ ...prev, co_authors: '' }));
  };

  const handleSave = () => {
    if (validateForm() && onSave && !isLoading && hasChanges) {
      setIsLoading(true);
      
      // Call onSave and handle the promise
      Promise.resolve(onSave(formData))
        .finally(() => {
          // Reset loading state when API call completes
          setIsLoading(false);
        });
    }
  };

  return (
    <div className="space-y-6">
      {/* Company Information Header */}
      <div>
        <h2 className="text-lg font-semibold text-[#0F2A4F] mb-4">Company Information</h2>
        {/* Name, Industry, and ERP Account Number */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter Company name"
              className={`w-full p-3 border ${formErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-md bg-transparent focus:outline-none focus:ring-1 focus:ring-deep-blue focus:border-deep-blue`}
            />
            {formErrors.name && (
              <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
            )}
          </div>
          <div>
            <input
              type="text"
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              placeholder="Enter Industry type"
              disabled
              className={`w-full p-3 border ${formErrors.industry ? 'border-red-500' : 'border-gray-300'} rounded-md bg-transparent focus:outline-none focus:ring-1 focus:ring-deep-blue focus:border-deep-blue`}
            />
            {formErrors.industry && (
              <p className="mt-1 text-sm text-red-500">{formErrors.industry}</p>
            )}
          </div>
          <div className="md:col-span-2 flex justify-end">
            <div className="flex w-full max-w-xl items-center justify-end gap-3">
              <label htmlFor="ERPAccountNumber" className="whitespace-nowrap text-sm font-medium text-[#0F2A4F]">
                ERP Account Number
              </label>
              <input
                type="text"
                id="ERPAccountNumber"
                name="ERPAccountNumber"
                value={formData.ERPAccountNumber || ''}
                onChange={handleChange}
                placeholder="Enter ERP account number"
                className="w-full max-w-sm p-3 border border-gray-300 rounded-md bg-transparent text-right focus:outline-none focus:ring-1 focus:ring-deep-blue focus:border-deep-blue"
              />
              <HelpButton onClick={() => setShowErpInfo(true)} className="shrink-0" />
            </div>
          </div>
        </div>
      </div>

      {/* Company Profile Header */}
      <div>
        <h2 className="text-lg font-semibold text-[#0F2A4F] mb-4">Company Profile</h2>
        {/* Company Profile */}
        <div className="mb-4">
          <textarea
            name="profile"
            value={formData.profile}
            onChange={handleChange}
            placeholder="Enter Company profile"
            maxLength={1700}
            rows={6}
            className={`w-full p-3 border ${formErrors.profile ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-deep-blue focus:border-deep-blue resize-none`}
          />
          <div className={`text-xs mt-2 text-left ${profileChars > 1700 ? 'text-red-500' : 'text-gray-500'}`}>
            {profileChars}/1700 chars remaining
          </div>
          {formErrors.profile && (
            <p className="mt-1 text-sm text-red-500">{formErrors.profile}</p>
          )}
        </div>
      </div>

      {/* Relationship Summary Header */}
      <div>
        <h2 className="text-lg font-semibold text-[#0F2A4F] mb-4">Relationship Summary</h2>
        <div className="mb-4">
          <textarea
            name="relationship"
            value={formData.relationship}
            onChange={handleChange}
            placeholder="Summarise our relationship with client"
            maxLength={1700}
            rows={6}
            className={`w-full p-3 border ${formErrors.relationship ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-deep-blue focus:border-deep-blue resize-none`}
          />
          <div className={`text-xs mt-2 text-left ${relationshipChars > 1700 ? 'text-red-500' : 'text-gray-500'}`}>
            {relationshipChars}/1700 chars remaining
          </div>
          {formErrors.relationship && (
            <p className="mt-1 text-sm text-red-500">{formErrors.relationship}</p>
          )}
        </div>
      </div>

      <div>
        <h2 className="text-lg font-semibold text-[#0F2A4F] mb-4">Co-Authors</h2>
        <div className="space-y-3">
          <div className="flex flex-col md:flex-row gap-3">
            <input
              type="email"
              value={coAuthorInput}
              onChange={(e) => {
                setCoAuthorInput(e.target.value);
                if (formErrors.co_authors) {
                  setFormErrors(prev => ({ ...prev, co_authors: '' }));
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCoAuthor();
                }
              }}
              placeholder="Add co-author email (name@presidio.com)"
              className={`w-full p-3 border ${formErrors.co_authors ? 'border-red-500' : 'border-gray-300'} rounded-md bg-transparent focus:outline-none focus:ring-1 focus:ring-deep-blue focus:border-deep-blue`}
            />
            <button
              type="button"
              onClick={handleAddCoAuthor}
              className="px-4 py-2 rounded-md border border-[#0F2A4F] text-[#0F2A4F] hover:bg-gray-50"
            >
              Add
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Co-authors with a valid `@presidio.com` email can edit this tech profile.
          </p>
          {formErrors.co_authors && (
            <p className="text-sm text-red-500">{formErrors.co_authors}</p>
          )}
          {(formData.co_authors || []).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {(formData.co_authors || []).map((coAuthor) => (
                <span
                  key={coAuthor.user_id || coAuthor.email}
                  className="inline-flex items-center gap-2 rounded-full bg-[#EAF0F8] px-3 py-1 text-sm text-[#0F2A4F]"
                >
                  {coAuthor.email}
                  <button
                    type="button"
                    onClick={() => handleRemoveCoAuthor(coAuthor.email)}
                    className="text-[#0F2A4F] hover:text-red-500"
                    aria-label={`Remove ${coAuthor.email}`}
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={isLoading || !hasChanges || exceedsCharacterLimit}
          className={`px-4 py-2 rounded-md flex items-center transition-all ${
            isLoading || !hasChanges || exceedsCharacterLimit
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
              : 'bg-[#0F2A4F] text-white hover:bg-opacity-90 cursor-pointer'
          }`}
        >
          {isLoading && (
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
          )}
          Save
        </button>
      </div>
      <InfoModal
        isOpen={showErpInfo}
        onClose={() => setShowErpInfo(false)}
        title="ERP Account Number"
        message="ERP Account Number is used to ensure we can pull information about this account from other Presidio systems"
      />
    </div>
  );
};
